/**
 * Menu contracts (03-modules.md §3, 04-data-model.md "menu", 05-api.md "Menu"):
 * categories, cuisines, products with variants, option groups and add-on links,
 * add-on groups, the one price model, availability (sold-out), validation,
 * publish/version, change history, the compact menu tills and QR menus read, and
 * the Excel import (preview diff -> apply) and export.
 *
 * Draft and published: every edit changes the outlet's DRAFT menu (the menu
 * tables) and bumps its draft revision. `publishMenu` validates the draft and
 * stores a snapshot of it as the next `menu_version`; tills, QR menus and the
 * server's pricing read only the published snapshot. Availability (sold-out) is
 * live: it applies to the published menu at once, without publishing.
 *
 * One price model: a price row is (target, context, section?, timing group?) ->
 * amount in paise + tax-inclusive flag. A row with no context is the target's
 * BASE price. Resolution for a context and section, first match wins:
 * section + timing group -> section -> context + timing group -> context -> base;
 * a variant (or option, add-on) without a price of its own in that chain falls
 * back to its product's chain (variants only). Timing groups are org's (days and
 * hours in the outlet's timezone).
 *
 * Money is integer paise (CLAUDE.md rule 2); ids are client-minted UUIDv7.
 */
import { z } from 'zod';
import { isoTimestampSchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema } from './money.js';
import { PRICE_CONTEXTS, timeOfDaySchema } from './org.js';

const atLeastOne = { message: 'Send at least one field to change' };
const sortSchema = z.int().min(0).max(10_000);
const archivable = {
  archivedAt: isoTimestampSchema.nullable(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
};

// ---------------------------------------------------------------------------
// Shared primitives

export const FOOD_TYPES = ['veg', 'non_veg', 'egg'] as const;
export type FoodType = (typeof FOOD_TYPES)[number];

/** What a price row prices. */
export const PRICE_TARGET_TYPES = ['product', 'variant', 'option', 'addon'] as const;
export type PriceTargetType = (typeof PRICE_TARGET_TYPES)[number];

/** What can be marked sold out. */
export const AVAILABILITY_TARGET_TYPES = ['product', 'variant', 'addon'] as const;
export type AvailabilityTargetType = (typeof AVAILABILITY_TARGET_TYPES)[number];

/** `all` or one pricing context (the order channel). */
export const AVAILABILITY_CHANNELS = ['all', ...PRICE_CONTEXTS] as const;
export type AvailabilityChannel = (typeof AVAILABILITY_CHANNELS)[number];

/** Channels a product can be hidden from (`product_channel_visibility`). */
export const VISIBILITY_CHANNELS = PRICE_CONTEXTS;

export const menuItemNameSchema = z.string().trim().min(1).max(120);
export const menuGroupNameSchema = z.string().trim().min(1).max(60);

/** A POS item code (Colour Tiles / item-code layouts, barcode shortcuts). */
export const itemCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9][A-Za-z0-9-]{0,19}$/, {
    message: 'Letters, digits and dashes, up to 20 characters',
  });

/** A category's tile colour on the till, `#RRGGBB` (stored uppercase). */
export const colourSchema = z
  .string()
  .trim()
  .regex(/^#[0-9A-Fa-f]{6}$/, { message: 'Expected a colour like #E85D2A' });

/** A colour as stored and returned (`#RRGGBB`, uppercase). */
const colourViewSchema = z.string();

const tagListSchema = z
  .array(z.string().trim().min(1).max(40))
  .max(20)
  .refine((v) => new Set(v.map((s) => s.toLowerCase())).size === v.length, {
    message: 'Each value at most once',
  });

/** 0 = not spicy … 3 = very spicy. */
export const spiceSchema = z.int().min(0).max(3);

/** `?includeArchived=true`. */
export const menuListQuerySchema = z.object({
  includeArchived: z.enum(['true', 'false']).optional(),
});

/** A price set inline with a product, variant, option or add-on: its BASE price. */
export const basePriceInputSchema = z.object({
  amountPaise: nonNegativePaiseSchema,
  taxInclusive: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Categories and cuisines

export const menuCategorySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  parentId: uuidSchema.nullable(),
  name: z.string(),
  sort: z.int(),
  colour: colourViewSchema.nullable(),
  /** org timing group (daypart): the category is served only then; null = always. */
  timingGroupId: uuidSchema.nullable(),
  ...archivable,
});
export type MenuCategoryView = z.output<typeof menuCategorySchema>;

const categoryFields = {
  name: menuGroupNameSchema,
  parentId: uuidSchema.nullable(),
  sort: sortSchema,
  colour: colourSchema.nullable(),
  timingGroupId: uuidSchema.nullable(),
};

export const createCategorySchema = z.object({
  id: uuidSchema.optional(),
  name: categoryFields.name,
  parentId: categoryFields.parentId.optional(),
  sort: sortSchema.optional(),
  colour: categoryFields.colour.optional(),
  timingGroupId: categoryFields.timingGroupId.optional(),
});
/** Any subset of a category's fields (the upsert command's `values`). */
export const categoryValuesSchema = z.object(categoryFields).partial();
export const updateCategorySchema = categoryValuesSchema.refine(
  (v) => Object.keys(v).length > 0,
  atLeastOne,
);
export const categoryParamsSchema = z.object({ outletId: uuidSchema, categoryId: uuidSchema });

/** Company-level (`cuisine`); listed under any outlet of the company. */
export const cuisineSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  ...archivable,
});
export type CuisineView = z.output<typeof cuisineSchema>;
export const createCuisineSchema = z.object({
  id: uuidSchema.optional(),
  name: menuGroupNameSchema,
});
export const updateCuisineSchema = z.object({ name: menuGroupNameSchema });
export const cuisineParamsSchema = z.object({ outletId: uuidSchema, cuisineId: uuidSchema });

// ---------------------------------------------------------------------------
// Prices

export const priceSchema = z.object({
  id: uuidSchema,
  targetType: z.enum(PRICE_TARGET_TYPES),
  targetId: uuidSchema,
  /** null = the base price. */
  context: z.enum(PRICE_CONTEXTS).nullable(),
  sectionId: uuidSchema.nullable(),
  timingGroupId: uuidSchema.nullable(),
  amountPaise: nonNegativePaiseSchema,
  taxInclusive: z.boolean(),
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type PriceView = z.output<typeof priceSchema>;

export const MAX_PRICES_PER_CALL = 2_000;

export const priceInputSchema = z
  .object({
    targetType: z.enum(PRICE_TARGET_TYPES),
    targetId: uuidSchema,
    /** null or absent = the base price. */
    context: z.enum(PRICE_CONTEXTS).nullable().optional(),
    sectionId: uuidSchema.nullable().optional(),
    timingGroupId: uuidSchema.nullable().optional(),
    /** null = remove this price (the next one in the resolution order applies). */
    amountPaise: nonNegativePaiseSchema.nullable(),
    /** Default false (tax added on top). */
    taxInclusive: z.boolean().optional(),
  })
  .refine(
    (p) =>
      (p.context !== undefined && p.context !== null) ||
      ((p.sectionId ?? null) === null && (p.timingGroupId ?? null) === null),
    {
      message: 'A section or timing-group price needs a context',
      path: ['context'],
    },
  );
export type PriceInput = z.output<typeof priceInputSchema>;

/** `PUT {o}/prices`: bulk upsert. */
export const setPricesSchema = z.object({
  prices: z.array(priceInputSchema).min(1).max(MAX_PRICES_PER_CALL),
});

export const priceListQuerySchema = z.object({
  targetId: uuidSchema.optional(),
  includeArchived: z.enum(['true', 'false']).optional(),
});

// ---------------------------------------------------------------------------
// Add-on groups

export const addonSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  foodType: z.enum(FOOD_TYPES).nullable(),
  sort: z.int(),
  archivedAt: isoTimestampSchema.nullable(),
});

export const addonGroupSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  minSelect: z.int(),
  maxSelect: z.int(),
  /** `freeCount` units of the cheapest add-ons chosen from this group are free (any order sent). */
  freeCount: z.int(),
  sort: z.int(),
  addons: z.array(addonSchema),
  ...archivable,
});
export type AddonGroupView = z.output<typeof addonGroupSchema>;

const selectCountSchema = z.int().min(0).max(50);

export const addonInputSchema = z.object({
  /** Keep an add-on's id to change it; a new (or no) id adds one. */
  id: uuidSchema.optional(),
  name: menuGroupNameSchema,
  foodType: z.enum(FOOD_TYPES).nullable().optional(),
  sort: sortSchema.optional(),
  price: basePriceInputSchema.optional(),
});

const addonListSchema = z
  .array(addonInputSchema)
  .max(100)
  .refine((v) => new Set(v.map((a) => a.name.toLowerCase())).size === v.length, {
    message: 'Each add-on name at most once',
  });

const addonGroupFields = {
  name: menuGroupNameSchema,
  minSelect: selectCountSchema,
  maxSelect: selectCountSchema,
  freeCount: selectCountSchema,
  sort: sortSchema,
  /** The group's complete add-on list: live add-ons not in it are archived. */
  addons: addonListSchema,
};

export const createAddonGroupSchema = z.object({
  id: uuidSchema.optional(),
  name: addonGroupFields.name,
  minSelect: selectCountSchema.optional(),
  maxSelect: selectCountSchema.optional(),
  freeCount: selectCountSchema.optional(),
  sort: sortSchema.optional(),
  addons: addonListSchema.optional(),
});
/** Any subset of an add-on group's fields (the upsert command's `values`). */
export const addonGroupValuesSchema = z.object(addonGroupFields).partial();
export const updateAddonGroupSchema = addonGroupValuesSchema.refine(
  (v) => Object.keys(v).length > 0,
  atLeastOne,
);
export const addonGroupParamsSchema = z.object({ outletId: uuidSchema, addonGroupId: uuidSchema });

// ---------------------------------------------------------------------------
// Products

export const variantSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sort: z.int(),
  archivedAt: isoTimestampSchema.nullable(),
});

export const optionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sort: z.int(),
  archivedAt: isoTimestampSchema.nullable(),
});

export const optionGroupSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  minSelect: z.int(),
  maxSelect: z.int(),
  sort: z.int(),
  options: z.array(optionSchema),
  archivedAt: isoTimestampSchema.nullable(),
});

export const productSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  categoryId: uuidSchema,
  cuisineId: uuidSchema.nullable(),
  name: z.string(),
  /** Item code for the till's code layouts and search. */
  shortCode: z.string().nullable(),
  description: z.string().nullable(),
  foodType: z.enum(FOOD_TYPES),
  spice: z.int().nullable(),
  allergens: z.array(z.string()),
  labels: z.array(z.string()),
  /** Sold at MRP (packaged goods): its prices must include tax. */
  isMrp: z.boolean(),
  portion: z.string().nullable(),
  prepMinutes: z.int().nullable(),
  /** HSN (goods) or SAC (services) code printed on the GST invoice; null = none. */
  hsnSac: z.string().nullable(),
  /** pricing's tax group (`assignTaxGroup`); null = no tax. */
  taxGroupId: uuidSchema.nullable(),
  /** Kept out of every discount. */
  nonDiscountable: z.boolean(),
  sort: z.int(),
  variants: z.array(variantSchema),
  optionGroups: z.array(optionGroupSchema),
  addonGroupIds: z.array(uuidSchema),
  /** Channels the product is hidden from (absent = shown everywhere). */
  hiddenChannels: z.array(z.enum(VISIBILITY_CHANNELS)),
  /** Every live price of the product, its variants and options. */
  prices: z.array(priceSchema),
  ...archivable,
});
export type ProductView = z.output<typeof productSchema>;

export const variantInputSchema = z.object({
  id: uuidSchema.optional(),
  name: z.string().trim().min(1).max(40),
  sort: sortSchema.optional(),
  price: basePriceInputSchema.optional(),
});

export const optionInputSchema = z.object({
  id: uuidSchema.optional(),
  name: menuGroupNameSchema,
  sort: sortSchema.optional(),
  /** Absent: the option is free unless a price row says otherwise. */
  price: basePriceInputSchema.optional(),
});

export const optionGroupInputSchema = z.object({
  id: uuidSchema.optional(),
  name: menuGroupNameSchema,
  minSelect: selectCountSchema.optional(),
  maxSelect: selectCountSchema.optional(),
  sort: sortSchema.optional(),
  options: z
    .array(optionInputSchema)
    .min(1)
    .max(50)
    .refine((v) => new Set(v.map((o) => o.name.toLowerCase())).size === v.length, {
      message: 'Each option name at most once',
    }),
});

const uniqueNames = <T extends { name: string }>(v: T[]) =>
  new Set(v.map((x) => x.name.toLowerCase())).size === v.length;

/**
 * HSN (goods: 4, 6 or 8 digits) or SAC (services: 6 digits starting 99, e.g. 996331
 * restaurant service) code for the GST invoice.
 */
export const hsnSacSchema = z
  .string()
  .trim()
  .regex(/^(\d{4}|\d{6}|\d{8})$/, { message: 'An HSN or SAC code: 4, 6 or 8 digits' });

const productFields = {
  categoryId: uuidSchema,
  cuisineId: uuidSchema.nullable(),
  name: menuItemNameSchema,
  shortCode: itemCodeSchema.nullable(),
  description: z.string().trim().max(1000).nullable(),
  foodType: z.enum(FOOD_TYPES),
  spice: spiceSchema.nullable(),
  allergens: tagListSchema,
  labels: tagListSchema,
  isMrp: z.boolean(),
  portion: z.string().trim().min(1).max(40).nullable(),
  prepMinutes: z.int().min(0).max(600).nullable(),
  hsnSac: hsnSacSchema.nullable(),
  /** Changing it needs `pricing.taxes.manage` as well (pricing's assignTaxGroup). */
  taxGroupId: uuidSchema.nullable(),
  nonDiscountable: z.boolean(),
  sort: sortSchema,
  /** The complete variant list (Half/Full): live variants not in it are archived. */
  variants: z.array(variantInputSchema).max(20).refine(uniqueNames, {
    message: 'Each variant name at most once',
  }),
  /** The complete option-group list. */
  optionGroups: z.array(optionGroupInputSchema).max(20).refine(uniqueNames, {
    message: 'Each option group name at most once',
  }),
  /** The add-on groups offered with it, in order. */
  addonGroupIds: z
    .array(uuidSchema)
    .max(20)
    .refine((v) => new Set(v).size === v.length, { message: 'Each add-on group at most once' }),
  hiddenChannels: z
    .array(z.enum(VISIBILITY_CHANNELS))
    .max(VISIBILITY_CHANNELS.length)
    .refine((v) => new Set(v).size === v.length, { message: 'Each channel at most once' }),
  /** The product's base price (needs `menu.prices` to change). */
  price: basePriceInputSchema,
};

export const createProductSchema = z.object({
  id: uuidSchema.optional(),
  categoryId: productFields.categoryId,
  name: productFields.name,
  foodType: productFields.foodType,
  cuisineId: productFields.cuisineId.optional(),
  shortCode: productFields.shortCode.optional(),
  description: productFields.description.optional(),
  spice: productFields.spice.optional(),
  allergens: productFields.allergens.optional(),
  labels: productFields.labels.optional(),
  isMrp: productFields.isMrp.optional(),
  portion: productFields.portion.optional(),
  prepMinutes: productFields.prepMinutes.optional(),
  hsnSac: productFields.hsnSac.optional(),
  taxGroupId: productFields.taxGroupId.optional(),
  nonDiscountable: productFields.nonDiscountable.optional(),
  sort: productFields.sort.optional(),
  variants: productFields.variants.optional(),
  optionGroups: productFields.optionGroups.optional(),
  addonGroupIds: productFields.addonGroupIds.optional(),
  hiddenChannels: productFields.hiddenChannels.optional(),
  price: productFields.price.optional(),
});
export type CreateProductInput = z.output<typeof createProductSchema>;

/** Any subset of a product's fields (the upsert command's `values`). */
export const productValuesSchema = z.object(productFields).partial();
export type ProductValues = z.output<typeof productValuesSchema>;
export const updateProductSchema = productValuesSchema.refine(
  (v) => Object.keys(v).length > 0,
  atLeastOne,
);

export const productParamsSchema = z.object({ outletId: uuidSchema, productId: uuidSchema });
export const productListQuerySchema = z.object({
  categoryId: uuidSchema.optional(),
  includeArchived: z.enum(['true', 'false']).optional(),
});

/** `GET {o}/products`: products without their prices (GET one for those). */
export const productSummarySchema = productSchema.omit({ prices: true });

// ---------------------------------------------------------------------------
// Availability (sold out)

export const availabilityTargetSchema = z.object({
  type: z.enum(AVAILABILITY_TARGET_TYPES),
  id: uuidSchema,
});
export type AvailabilityTarget = z.output<typeof availabilityTargetSchema>;

export const MAX_AVAILABILITY_TARGETS = 500;

/** `POST {o}/availability` (`menu.setAvailability`). */
export const setAvailabilitySchema = z
  .object({
    targets: z
      .array(availabilityTargetSchema)
      .min(1)
      .max(MAX_AVAILABILITY_TARGETS)
      .refine((v) => new Set(v.map((t) => `${t.type}:${t.id}`)).size === v.length, {
        message: 'Each target at most once',
      }),
    /** Default `['all']`. */
    channels: z
      .array(z.enum(AVAILABILITY_CHANNELS))
      .min(1)
      .max(AVAILABILITY_CHANNELS.length)
      .refine((v) => new Set(v).size === v.length, { message: 'Each channel at most once' })
      .optional(),
    available: z.boolean(),
    /** Sold out until this instant (then available again by itself); only with `available: false`. */
    until: isoTimestampSchema.optional(),
  })
  .refine((v) => v.until === undefined || !v.available, {
    message: 'until only applies when marking sold out',
    path: ['until'],
  });

export const availabilityEntrySchema = z.object({
  targetType: z.enum(AVAILABILITY_TARGET_TYPES),
  targetId: uuidSchema,
  channel: z.enum(AVAILABILITY_CHANNELS),
  available: z.boolean(),
  until: isoTimestampSchema.nullable(),
  changedBy: uuidSchema.nullable(),
  changedAt: isoTimestampSchema,
});
export type AvailabilityEntryView = z.output<typeof availabilityEntrySchema>;

export const setAvailabilityResponseSchema = z.object({
  availabilityVersion: z.int(),
  entries: z.array(availabilityEntrySchema),
});

/** `GET {o}/availability`: what is sold out now (expired `until` entries left out). */
export const availabilityListSchema = z.object({
  availabilityVersion: z.int(),
  data: z.array(availabilityEntrySchema),
});

// ---------------------------------------------------------------------------
// Validate, publish, status, versions, history

export const MENU_PROBLEM_SEVERITIES = ['error', 'warning'] as const;

export const menuProblemSchema = z.object({
  severity: z.enum(MENU_PROBLEM_SEVERITIES),
  /** Stable code, e.g. `PRODUCT_NO_PRICE`. */
  code: z.string(),
  targetType: z.string(),
  targetId: uuidSchema.nullable(),
  message: z.string(),
});
export type MenuProblem = z.output<typeof menuProblemSchema>;

export const menuValidationSchema = z.object({
  /** No errors: the draft can be published. */
  ok: z.boolean(),
  draftRevision: z.int(),
  problems: z.array(menuProblemSchema),
});

export const publishMenuSchema = z.object({
  notes: z.string().trim().max(200).optional(),
});

export const menuVersionSchema = z.object({
  version: z.int(),
  draftRevision: z.int(),
  publishedAt: isoTimestampSchema,
  publishedBy: uuidSchema.nullable(),
  productCount: z.int(),
  notes: z.string().nullable(),
  /** Warnings the validation found when it was published. */
  warnings: z.array(menuProblemSchema),
});

export const menuStatusSchema = z.object({
  /** Bumped by every draft change. */
  draftRevision: z.int(),
  /** null until the first publish. */
  publishedVersion: z.int().nullable(),
  publishedRevision: z.int().nullable(),
  publishedAt: isoTimestampSchema.nullable(),
  hasUnpublishedChanges: z.boolean(),
  /** Bumped by every availability change. */
  availabilityVersion: z.int(),
});
export type MenuStatus = z.output<typeof menuStatusSchema>;

export const menuVersionListSchema = z.object({ data: z.array(menuVersionSchema) });

export const MENU_CHANGE_ACTIONS = [
  'created',
  'updated',
  'archived',
  'prices',
  'availability',
  'tax_group',
  'imported',
  'published',
] as const;

export const menuChangeSchema = z.object({
  id: uuidSchema,
  at: isoTimestampSchema,
  actor: z.object({ kind: z.string(), id: uuidSchema.nullable() }),
  targetType: z.string(),
  targetId: uuidSchema.nullable(),
  action: z.enum(MENU_CHANGE_ACTIONS),
  /** What changed: the fields before and after (only the changed ones). */
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  draftRevision: z.int(),
  requestId: z.string().nullable(),
});
export type MenuChangeView = z.output<typeof menuChangeSchema>;

export const menuHistoryQuerySchema = z.object({
  targetId: uuidSchema.optional(),
  limit: z.preprocess(
    (v) => (typeof v === 'string' && /^\d+$/.test(v.trim()) ? Number(v.trim()) : v),
    z.number().int().min(1).max(200).default(50),
  ),
  cursor: z.string().min(1).max(100).optional(),
});

// ---------------------------------------------------------------------------
// Published snapshot (stored as menu_version.snapshot): the whole live draft at
// publish time, every context's prices included. Wire form (paise as numbers).

const snapshotPriceSchema = z.object({
  targetType: z.enum(PRICE_TARGET_TYPES),
  targetId: uuidSchema,
  context: z.enum(PRICE_CONTEXTS).nullable(),
  sectionId: uuidSchema.nullable(),
  timingGroupId: uuidSchema.nullable(),
  amountPaise: nonNegativePaiseSchema,
  taxInclusive: z.boolean(),
});
export type SnapshotPrice = z.output<typeof snapshotPriceSchema>;

export const timingWindowSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** ISO weekdays, 1 = Monday. */
  days: z.array(z.int().min(1).max(7)),
  startTime: timeOfDaySchema,
  /** Before `startTime`: the window runs past midnight (and belongs to its start day). */
  endTime: timeOfDaySchema,
  sort: z.int(),
});
export type TimingWindow = z.output<typeof timingWindowSchema>;

export const menuSnapshotSchema = z.object({
  categories: z.array(
    z.object({
      id: uuidSchema,
      parentId: uuidSchema.nullable(),
      name: z.string(),
      sort: z.int(),
      colour: z.string().nullable(),
      timingGroupId: uuidSchema.nullable(),
    }),
  ),
  products: z.array(
    z.object({
      id: uuidSchema,
      categoryId: uuidSchema,
      cuisine: z.string().nullable(),
      name: z.string(),
      shortCode: z.string().nullable(),
      description: z.string().nullable(),
      foodType: z.enum(FOOD_TYPES),
      spice: z.int().nullable(),
      allergens: z.array(z.string()),
      labels: z.array(z.string()),
      isMrp: z.boolean(),
      portion: z.string().nullable(),
      prepMinutes: z.int().nullable(),
      taxGroupId: uuidSchema.nullable(),
      nonDiscountable: z.boolean(),
      sort: z.int(),
      hiddenChannels: z.array(z.enum(VISIBILITY_CHANNELS)),
      variants: z.array(z.object({ id: uuidSchema, name: z.string(), sort: z.int() })),
      optionGroups: z.array(
        z.object({
          id: uuidSchema,
          name: z.string(),
          minSelect: z.int(),
          maxSelect: z.int(),
          sort: z.int(),
          options: z.array(z.object({ id: uuidSchema, name: z.string(), sort: z.int() })),
        }),
      ),
      addonGroupIds: z.array(uuidSchema),
    }),
  ),
  addonGroups: z.array(
    z.object({
      id: uuidSchema,
      name: z.string(),
      minSelect: z.int(),
      maxSelect: z.int(),
      freeCount: z.int(),
      sort: z.int(),
      addons: z.array(
        z.object({
          id: uuidSchema,
          name: z.string(),
          foodType: z.enum(FOOD_TYPES).nullable(),
          sort: z.int(),
        }),
      ),
    }),
  ),
  prices: z.array(snapshotPriceSchema),
  /** The timing groups categories and prices refer to. */
  timingGroups: z.array(timingWindowSchema),
});
export type MenuSnapshot = z.output<typeof menuSnapshotSchema>;

/** A snapshot in its stored (jsonb) form: paise as numbers. */
export function encodeMenuSnapshot(snapshot: MenuSnapshot): unknown {
  return z.encode(menuSnapshotSchema, snapshot);
}

// ---------------------------------------------------------------------------
// Compact menu (`GET {o}/menu?context=&sectionId=`): the published menu for one
// context (and section), prices resolved down to candidates the till picks from
// by time of day, with live availability. Cacheable; strong ETag.

/**
 * A target's price candidates for this context and section, in resolution order:
 * the first whose `timingGroupId` is null or active now applies. Empty: no price.
 */
export const priceCandidateSchema = z.object({
  amountPaise: nonNegativePaiseSchema,
  taxInclusive: z.boolean(),
  timingGroupId: uuidSchema.nullable(),
});
export type PriceCandidate = z.output<typeof priceCandidateSchema>;

const availabilityStateSchema = {
  available: z.boolean(),
  /** When sold out with an end: available again from then. */
  unavailableUntil: isoTimestampSchema.nullable(),
};

export const compactMenuSchema = z.object({
  outletId: uuidSchema,
  context: z.enum(PRICE_CONTEXTS),
  sectionId: uuidSchema.nullable(),
  menuVersion: z.int(),
  availabilityVersion: z.int(),
  publishedAt: isoTimestampSchema,
  /** The outlet's timezone (timing groups are wall-clock times there). */
  timezone: z.string(),
  timingGroups: z.array(timingWindowSchema),
  categories: z.array(
    z.object({
      id: uuidSchema,
      parentId: uuidSchema.nullable(),
      name: z.string(),
      sort: z.int(),
      colour: z.string().nullable(),
      /** Served only while this timing group (or a parent category's) is active. */
      timingGroupId: uuidSchema.nullable(),
    }),
  ),
  products: z.array(
    z.object({
      id: uuidSchema,
      categoryId: uuidSchema,
      cuisine: z.string().nullable(),
      name: z.string(),
      shortCode: z.string().nullable(),
      description: z.string().nullable(),
      foodType: z.enum(FOOD_TYPES),
      spice: z.int().nullable(),
      allergens: z.array(z.string()),
      labels: z.array(z.string()),
      isMrp: z.boolean(),
      portion: z.string().nullable(),
      prepMinutes: z.int().nullable(),
      taxGroupId: uuidSchema.nullable(),
      discountable: z.boolean(),
      sort: z.int(),
      prices: z.array(priceCandidateSchema),
      variants: z.array(
        z.object({
          id: uuidSchema,
          name: z.string(),
          sort: z.int(),
          prices: z.array(priceCandidateSchema),
          ...availabilityStateSchema,
        }),
      ),
      optionGroups: z.array(
        z.object({
          id: uuidSchema,
          name: z.string(),
          minSelect: z.int(),
          maxSelect: z.int(),
          sort: z.int(),
          options: z.array(
            z.object({
              id: uuidSchema,
              name: z.string(),
              sort: z.int(),
              prices: z.array(priceCandidateSchema),
            }),
          ),
        }),
      ),
      addonGroupIds: z.array(uuidSchema),
      ...availabilityStateSchema,
    }),
  ),
  addonGroups: z.array(
    z.object({
      id: uuidSchema,
      name: z.string(),
      minSelect: z.int(),
      maxSelect: z.int(),
      freeCount: z.int(),
      sort: z.int(),
      addons: z.array(
        z.object({
          id: uuidSchema,
          name: z.string(),
          foodType: z.enum(FOOD_TYPES).nullable(),
          sort: z.int(),
          prices: z.array(priceCandidateSchema),
          ...availabilityStateSchema,
        }),
      ),
    }),
  ),
});
export type CompactMenu = z.output<typeof compactMenuSchema>;
export type CompactProduct = CompactMenu['products'][number];
export type CompactAddonGroup = CompactMenu['addonGroups'][number];

export const compactMenuQuerySchema = z.object({
  context: z.enum(PRICE_CONTEXTS),
  sectionId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// Excel import and export

/** Largest .xlsx accepted (bytes, before base64). */
export const MENU_IMPORT_MAX_BYTES = 700 * 1024;

export const menuImportRequestSchema = z.object({
  fileName: z.string().trim().min(1).max(200).optional(),
  /** The .xlsx file, base64. */
  contentBase64: z
    .string()
    .min(4)
    .max(Math.ceil(MENU_IMPORT_MAX_BYTES / 3) * 4 + 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, { message: 'Expected base64' }),
  /** Archive live categories, products and add-on groups the file does not list. Default false. */
  archiveMissing: z.boolean().optional(),
});

export const IMPORT_ENTITIES = ['category', 'addon_group', 'product', 'price'] as const;

export const importChangeSchema = z.object({
  entity: z.enum(IMPORT_ENTITIES),
  action: z.enum(['add', 'change', 'archive']),
  /** The row's name (product, group, …) as the file has it. */
  name: z.string(),
  targetId: uuidSchema.nullable(),
  /** Changed fields (for `change`). */
  fields: z.array(z.string()),
});

export const importRowErrorSchema = z.object({
  sheet: z.string(),
  /** 1-based spreadsheet row; 0 = the sheet as a whole. */
  row: z.int(),
  column: z.string().nullable(),
  message: z.string(),
});

const countsSchema = z.object({ add: z.int(), change: z.int(), archive: z.int() });

export const menuImportPreviewSchema = z.object({
  id: uuidSchema,
  status: z.enum(['previewed', 'applied']),
  /** The draft revision the diff is against; apply refuses if the draft moved on. */
  baseRevision: z.int(),
  expiresAt: isoTimestampSchema,
  /** No row errors and something to change. */
  canApply: z.boolean(),
  summary: z.object({
    categories: countsSchema,
    addonGroups: countsSchema,
    products: countsSchema,
    prices: countsSchema,
  }),
  /** The changes (the first 2,000). */
  changes: z.array(importChangeSchema),
  errors: z.array(importRowErrorSchema),
});
export type MenuImportPreview = z.output<typeof menuImportPreviewSchema>;

export const menuImportParamsSchema = z.object({ outletId: uuidSchema, importId: uuidSchema });

export const menuImportApplyResponseSchema = z.object({
  importId: uuidSchema,
  draftRevision: z.int(),
  summary: menuImportPreviewSchema.shape.summary,
});

// ---------------------------------------------------------------------------
// pricing's assignTaxGroup (03-modules.md §6) on menu products

export const assignTaxGroupSchema = z.object({
  productIds: z
    .array(uuidSchema)
    .min(1)
    .max(1_000)
    .refine((v) => new Set(v).size === v.length, { message: 'Each product at most once' }),
});
export const assignTaxGroupResponseSchema = z.object({
  taxGroupId: uuidSchema,
  /** Products whose tax group changed (the others had it already). */
  changed: z.array(uuidSchema),
});

// ---------------------------------------------------------------------------
// Events

const masterChange = z.enum(['created', 'updated', 'archived']);

export const menuCategoryChangedV1 = defineEvent(
  'menu_category.changed',
  1,
  z.object({ categoryId: uuidSchema, change: masterChange, version: z.int() }),
);

/** Company-level (outletId null). */
export const cuisineChangedV1 = defineEvent(
  'cuisine.changed',
  1,
  z.object({ cuisineId: uuidSchema, change: masterChange, version: z.int() }),
);

export const addonGroupChangedV1 = defineEvent(
  'addon_group.changed',
  1,
  z.object({ addonGroupId: uuidSchema, change: masterChange, version: z.int() }),
);

/** A draft product changed (03-modules.md §3 `product.updated`); `tax_group` = assignTaxGroup. */
export const productUpdatedV1 = defineEvent(
  'product.updated',
  1,
  z.object({
    productId: uuidSchema,
    change: z.enum(['created', 'updated', 'archived', 'tax_group']),
    fields: z.array(z.string()),
    version: z.int(),
  }),
);

export const menuPricesChangedV1 = defineEvent(
  'menu.prices_changed',
  1,
  z.object({
    targets: z.array(z.object({ type: z.enum(PRICE_TARGET_TYPES), id: uuidSchema })),
    draftRevision: z.int(),
  }),
);

export const menuImportPreviewedV1 = defineEvent(
  'menu_import.previewed',
  1,
  z.object({ importId: uuidSchema, canApply: z.boolean(), errors: z.int() }),
);

export const menuImportedV1 = defineEvent(
  'menu.imported',
  1,
  z.object({
    importId: uuidSchema,
    draftRevision: z.int(),
    summary: menuImportPreviewSchema.shape.summary,
  }),
);

/** A new menu version is live (03-modules.md §3). */
export const menuPublishedV1 = defineEvent(
  'menu.published',
  1,
  z.object({
    version: z.int(),
    previousVersion: z.int().nullable(),
    draftRevision: z.int(),
    productCount: z.int(),
  }),
);

/** Sold out or back (03-modules.md §3; F6). */
export const itemAvailabilityChangedV1 = defineEvent(
  'item.availability_changed',
  1,
  z.object({
    targets: z.array(availabilityTargetSchema),
    channels: z.array(z.enum(AVAILABILITY_CHANNELS)),
    available: z.boolean(),
    until: isoTimestampSchema.nullable(),
    availabilityVersion: z.int(),
  }),
);

export const MENU_EVENTS = [
  menuCategoryChangedV1,
  cuisineChangedV1,
  addonGroupChangedV1,
  productUpdatedV1,
  menuPricesChangedV1,
  menuImportPreviewedV1,
  menuImportedV1,
  menuPublishedV1,
  itemAvailabilityChangedV1,
] as const;
